/**
 * Screen-space wind arrows — ported from `combo_wind_arrows.dart`. A canvas over
 * the map draws a fixed-pixel arrow grid (constant on-screen density across
 * zoom): each cell centre is unprojected to lat/lon and sampled from the
 * displayed forecast frame.
 *
 * Performance notes:
 *  - MapLibre `render` fires 60+ times/sec during a zoom animation. We coalesce
 *    those into ONE canvas paint per animation frame via `requestAnimationFrame`
 *    so the arrows never do more work than the display can show.
 *  - The whole arrow grid is drawn as ONE stroked Path (single `beginPath` /
 *    `stroke`), not one stroke per arrow (~2 500 arrows on a laptop). One GPU
 *    submit is ~10× cheaper than 2 500.
 *  - We only recompute the U/V field when the frame or field object identity
 *    changes.
 */
import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import type * as maplibregl from 'maplibre-gl';
import { Subscription } from 'rxjs';

import { ComboService, ComboState, curArrowIndex } from './combo.service';
import { ComboConstants, hexToRgb } from './combo.constants';
import { fieldFromCurGrid, WindField } from './combo-wind';
import { encodeGridUV, interpField } from './combo-wind-math';

/** Per-layer arrow draw parameters, mirroring the app's `ComboArrowsOverlay`. */
interface ArrowSpec {
  field: WindField;
  frameIndex: number;
  /** #RRGGBB ink; wind=accent blue, currents=white (reads over the turbo raster). */
  color: string;
  /** Currents drop a cell whose enclosing grid touches land (NaN). */
  dropOverLand: boolean;
  /** Currents rotate every bearing +180° so arrows point downstream. */
  dirOffsetDeg: number;
}

@Component({
  selector: 'app-combo-wind-arrows',
  standalone: true,
  imports: [CommonModule],
  template: `<canvas #cv class="combo-wind-canvas"></canvas>`,
  styles: [
    `:host { position: absolute; inset: 0; pointer-events: none; z-index: 5; }
     .combo-wind-canvas { position: absolute; inset: 0; width: 100%; height: 100%; }`,
  ],
})
export class ComboWindArrowsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() map: maplibregl.Map | null = null;
  @ViewChild('cv', { static: true }) cv!: ElementRef<HTMLCanvasElement>;

  private state: ComboState | null = null;
  private sub?: Subscription;

  // Encoded U/V for the current frame (recomputed only on frame/field change).
  private uv: { u: number[][]; v: number[][] } | null = null;
  private uvFrame = -1;
  private uvFieldRef: any = null;
  private uvDirOffset = 0;

  private lastZoom: number | null = null;

  /** Sticky flags — arrows only draw once the coloured overlay image has painted. */
  private windLayerHasPainted = false;
  private curLayerHasPainted = false;
  private readonly WIND_SOURCE_ID = 'combo-wind-source';
  /** Currents draw over the manifest turbo raster (the shared value source). */
  private readonly VALUE_SOURCE_ID = 'combo-value-source';

  /** rAF scheduling: one paint per animation frame regardless of event volume. */
  private rafId: number | null = null;
  private dirty = false;

  constructor(private combo: ComboService) {}

  ngOnInit(): void {
    this.sub = this.combo.state$.subscribe((s) => {
      this.state = s;
      this.scheduleDraw();
    });
    this.attach();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['map']) {
      this.detach();
      this.attach();
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.detach();
    if (this.rafId != null) cancelAnimationFrame(this.rafId);
  }

  private renderHandler = () => this.scheduleDraw();
  private sourceDataHandler = (ev: any) => {
    if (
      (ev?.sourceId === this.WIND_SOURCE_ID || ev?.sourceId === this.VALUE_SOURCE_ID) &&
      ev?.isSourceLoaded
    ) {
      this.scheduleDraw();
    }
  };

  /**
   * The arrow field + params for the active layer, or null (nothing to draw).
   * Wind uses the baked forecast field; currents use the per-timestep cube
   * (or the single-snapshot grid, wrapped as a 1-step field) — white ink,
   * land-drop, and a +180° bearing so arrows point downstream.
   */
  private activeSpec(s: ComboState | null): ArrowSpec | null {
    if (s == null || !s.config.enabled) return null;
    const layer = s.config.layer;
    if (layer === 'wind') {
      if (s.windField == null || s.windFrames.length === 0) return null;
      return {
        field: s.windField,
        frameIndex: s.windFrameIndex,
        color: ComboConstants.windArrowColor,
        dropOverLand: false,
        dirOffsetDeg: 0,
      };
    }
    if (layer === 'cur') {
      if (s.curField != null) {
        return {
          field: s.curField,
          frameIndex: curArrowIndex(s),
          color: '#FFFFFF',
          dropOverLand: true,
          dirOffsetDeg: 180,
        };
      }
      if (s.curGrid != null) {
        return {
          field: fieldFromCurGrid(s.curGrid),
          frameIndex: 0,
          color: '#FFFFFF',
          dropOverLand: true,
          dirOffsetDeg: 180,
        };
      }
      return null;
    }
    return null;
  }

  private attach(): void {
    if (!this.map) return;
    this.map.on('render', this.renderHandler);
    this.map.on('sourcedata', this.sourceDataHandler);
    this.scheduleDraw();
  }
  private detach(): void {
    if (!this.map) return;
    this.map.off('render', this.renderHandler);
    this.map.off('sourcedata', this.sourceDataHandler);
  }

  /**
   * Mark as needing a repaint; the actual `draw()` runs at most once per rAF.
   * MapLibre fires `render` many times per animation tick during zoom/pan —
   * without this coalescing, we'd redo 2 500 unproject+interp+canvas ops per
   * event, freezing the main thread. With it, we do the work exactly as often
   * as the display can show a new frame.
   */
  private scheduleDraw(): void {
    this.dirty = true;
    if (this.rafId != null) return;
    this.rafId = requestAnimationFrame(() => {
      this.rafId = null;
      if (!this.dirty) return;
      this.dirty = false;
      this.draw();
    });
  }

  private ensureEncoded(spec: ArrowSpec): void {
    const f = spec.field;
    if (f.speed.length === 0) {
      this.uv = null;
      return;
    }
    const idx = Math.max(0, Math.min(f.speed.length - 1, spec.frameIndex));
    if (
      this.uvFieldRef === f &&
      this.uvFrame === idx &&
      this.uvDirOffset === spec.dirOffsetDeg &&
      this.uv != null
    ) {
      return;
    }
    this.uv =
      idx >= 0 && idx < f.speed.length
        ? encodeGridUV(f.speed[idx], f.dir[idx], spec.dirOffsetDeg)
        : null;
    this.uvFieldRef = f;
    this.uvFrame = idx;
    this.uvDirOffset = spec.dirOffsetDeg;
  }

  private draw(): void {
    const canvas = this.cv?.nativeElement;
    const map = this.map;
    const s = this.state;
    if (!canvas || !map) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = map.getContainer().clientWidth;
    const h = map.getContainer().clientHeight;
    if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const spec = this.activeSpec(s);

    const layer = s?.config.layer;

    // Reset the wind "already painted" latch whenever we're not actively drawing
    // wind arrows, so the next wind activation waits for the field paint again.
    if (spec == null || layer !== 'wind') {
      if (this.windLayerHasPainted) {
        this.windLayerHasPainted = false;
        this.combo.setWindFieldPainted(false);
      }
    }
    // Reset the currents latch whenever we're not drawing currents arrows.
    if (spec == null || layer !== 'cur') {
      this.curLayerHasPainted = false;
    }
    if (spec == null) return;

    // Both wind and currents gate their arrows on the COLOURED OVERLAY IMAGE
    // having actually painted on the map — never draw arrows over a blank/loading
    // map. Wind waits for its baked field source; currents wait for the manifest
    // turbo raster (the shared value source). Each latch is sticky so playback /
    // scrub image swaps don't flicker the arrows off once the layer is up.
    if (layer === 'wind') {
      if (!this.windLayerHasPainted) {
        if (!this.sourceReady(this.WIND_SOURCE_ID)) return;
        this.windLayerHasPainted = true;
        this.combo.setWindFieldPainted(true);
      }
    } else if (layer === 'cur') {
      if (!this.curLayerHasPainted) {
        if (!this.sourceReady(this.VALUE_SOURCE_ID)) return;
        this.curLayerHasPainted = true;
      }
    }

    this.ensureEncoded(spec);
    const uv = this.uv;
    if (uv == null) return;

    // Pulse the caption hint on zoom change.
    const zoom = map.getZoom();
    if (this.lastZoom != null && Math.abs(zoom - this.lastZoom) >= 0.01) {
      this.combo.pulseWindArrowsBusy();
    }
    this.lastZoom = zoom;

    // Fixed 22 px spacing → density matches mobile regardless of viewport width.
    const SPACING_PX = 22;
    if (w <= 0 || h <= 0) return;
    const cols = Math.max(1, Math.round(w / SPACING_PX));
    const spacing = w / cols;
    const rows = Math.ceil(h / spacing);

    const opacity = Math.max(0, Math.min(1, s!.config.opacity));
    ctx.strokeStyle = this.rgbaInk(spec.color, opacity);
    ctx.lineWidth = ComboConstants.windArrowStroke;
    ctx.lineCap = 'round';

    const shaft = ComboConstants.windArrowShaft;
    const head = ComboConstants.windArrowHead;
    const f = spec.field;
    const dropOverLand = spec.dropOverLand;

    // ── ONE path for all arrows → one stroke() call, not ~2 500. ────────────
    ctx.beginPath();
    for (let gi = 0; gi < rows; gi++) {
      const py = (gi + 0.5) * spacing;
      for (let gj = 0; gj < cols; gj++) {
        const px = (gj + 0.5) * spacing;
        const ll = map.unproject([px, py]);
        const r = interpField(ll.lat, ll.lng, uv.u, uv.v, f.lats, f.lons, dropOverLand);
        if (r == null) continue;
        const th = ((r.dir + 180) * Math.PI) / 180; // points downwind
        const dx = Math.sin(th);
        const dy = -Math.cos(th);
        const tipX = px + dx * shaft;
        const tipY = py + dy * shaft;
        const tailX = px - dx * shaft;
        const tailY = py - dy * shaft;
        // shaft
        ctx.moveTo(tailX, tailY);
        ctx.lineTo(tipX, tipY);
        // two arrowhead barbs
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - head * Math.sin(th + 2.5), tipY + head * Math.cos(th + 2.5));
        ctx.moveTo(tipX, tipY);
        ctx.lineTo(tipX - head * Math.sin(th - 2.5), tipY + head * Math.cos(th - 2.5));
      }
    }
    ctx.stroke();
  }

  /** True once MapLibre has actually loaded the given image source. */
  private sourceReady(id: string): boolean {
    const map = this.map;
    if (!map) return false;
    const src = map.getSource(id);
    return !!src && map.isSourceLoaded(id);
  }

  private rgbaInk(hex: string, opacity: number): string {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
}
